import React, { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import api from '../utils/axios';

// Sub-components
import TopBar from './landing/TopBar';
import Header from './landing/Header';
import Hero from './landing/Hero';
import CourseSections from './landing/CourseSections';
import GallerySlider from './landing/GallerySlider';
import LogoMarquee from './landing/LogoMarquee';
import Footer from './landing/Footer';
import EnquiryModal from './landing/EnquiryModal';
import FounderTalk from './landing/FounderTalk';
import SuccessMarquee from './landing/SuccessMarquee';
import BlogSection from './landing/BlogSection';
import StudentVoiceWall from './landing/StudentVoiceWall';
import AnnouncementPopup from './landing/AnnouncementPopup';

const Landing = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [inquiryContext, setInquiryContext] = useState(null);
    const [content, setContent] = useState({});

    const handleInquiry = (context = null) => {
        setInquiryContext(context);
        setIsInquiryOpen(true);
    };

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    const affiliationLogos = (() => {
        try {
            return content.home_affiliations ? JSON.parse(content.home_affiliations) : [];
        } catch(e) {
            return [];
        }
    })();

    const courseCards = (() => {
        try {
            return content.home_course_cards ? JSON.parse(content.home_course_cards) : [];
        } catch(e) {
            return [];
        }
    })();

    const studentSpotlightData = (() => {
        try {
            return content.home_student_spotlight ? JSON.parse(content.home_student_spotlight) : [];
        } catch(e) {
            return [];
        }
    })();


    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-clip">
            
            {/* 1. TOP INFO BAR & NEWS TICKER */}
            {!isScrolled && <TopBar data={content} />}

            {/* 2. MAIN NAVIGATION */}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => handleInquiry()}
                logo={content.site_logo}
                colleges={content.colleges_list}
                applyUrl={content.apply_url || ''}
                email={content.contact_email}
                phone={content.contact_phone}
            />

            {/* 3. HERO SLIDER */}
            <Hero data={{ 
                title: content.hero_title, 
                image: content.hero_image,
                video: content.hero_video,
                bgType: content.hero_bg_type,
                badge: content.hero_badge,
                description: content.hero_description,
                btn1Text: content.hero_btn1_text,
                btn2Text: content.hero_btn2_text,
                years: content.stats_years,
                alumni: content.stats_alumni,
                campuses: content.stats_campuses,
                faculty: content.stats_faculty
            }} />

            {/* 5. CONTENT SECTION / FOUNDER TALK */}
            <FounderTalk data={{
                name: content.founder_name,
                role: content.founder_role,
                image: content.founder_image,
                message: content.founder_message,
                badge: content.founder_title_badge,
                years: content.stats_years
            }} />


            <CourseSections 
                onInquiry={(title) => handleInquiry(title)}
                courses={courseCards}
                data={{ title: content.course_section_title, highlight: content.course_section_highlight }} 
            />

            {/* 7. PHOTO GALLERY SLIDER (Moments of Excellence) */}
            <GallerySlider images={(() => {
                try {
                    return content.gallery_images ? JSON.parse(content.gallery_images) : [];
                } catch(e) {
                    return [];
                }
            })()} />

            {/* 7.2 AFFILIATIONS */}
            <LogoMarquee 
                logos={affiliationLogos} 
                title="Institutional" 
                highlight="Affiliation" 
                description="Our educational standards are recognized and validated by premier academic and regulatory bodies."
            />
            {/* 7. BLOG & NEWS */}
            {content.blog_enabled === 'true' && (
                <BlogSection 
                    posts={(() => {
                        try {
                            return content.blog_posts ? JSON.parse(content.blog_posts) : [];
                        } catch(e) { return []; }
                    })()} 
                />
            )}

            {/* 7.2 STUDENT VOICE WALL */}
            <StudentVoiceWall />

            {/* 7.3 RECRUITERS */}
            {content.home_recruiters_enabled === 'true' && (
                <LogoMarquee 
                    logos={(() => {
                        try {
                            return content.placements_recruiters ? JSON.parse(content.placements_recruiters) : [];
                        } catch(e) {
                            return [];
                        }
                    })()} 
                    title="Prominent" 
                    highlight="Recruiters" 
                    description="Our students are recruited by global corporations, leading the way in industry-driven employability."
                />
            )}

            {/* 7.3 EVENTS */}
            {/* <Events /> */}

            {/* 7.35 NOTICE BOARD */}
            {/* <NoticeBoard /> */}

            {/* 7.4 PLACEMENT MARQUEE (ADMIN CONTROLLED) */}
            {content.placements_marquee_enabled === 'true' && (
                <SuccessMarquee stories={(() => {
                    try {
                        return content.placements_success_stories ? JSON.parse(content.placements_success_stories) : [];
                    } catch(e) {
                        return [];
                    }
                })()} />
            )}

            {/* 8. FOOTER */}
            <Footer data={{...content, student_spotlight: studentSpotlightData}} />

            {/* 9. ADMISSION ENQUIRY TAB (Techno India Style) */}
            <button 
                onClick={() => handleInquiry()}
                className="hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 bg-[#cc0000] text-white py-6 px-3 flex-col items-center space-y-4 rounded-l-md shadow-2xl z-40 hover:bg-[#003366] transition-all group border-b-4 border-amber-400"
            >
                <Mail size={16} className="md:w-5 md:h-5 group-hover:scale-110 transition-transform" />
                <span className="[writing-mode:vertical-lr] rotate-180 uppercase font-black text-[8px] md:text-[10px] tracking-[2px] md:tracking-[4px]">Admission Enquiry</span>
            </button>

            <EnquiryModal 
                isOpen={isInquiryOpen} 
                onClose={() => setIsInquiryOpen(false)} 
                initialProgram={inquiryContext}
            />

            {/* 10. ANNOUNCEMENT POPUP (ADMIN CONTROLLED) */}
            <AnnouncementPopup data={content} />
        </div>
    );
};

export default Landing;
